import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertTriangle, Lock, X, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { cn } from '@/lib/utils';

export function TrialBanner() {
  const { isTrialing, isInGracePeriod, isLocked, daysRemaining, status, isLoading } = useTrialStatus();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (isLoading) return null;

  // Active/paid subscriptions don't need a banner
  if (status === 'active') return null;

  // Allow dismissal during active trial (not grace/locked)
  if (dismissed && isTrialing) return null;

  const goToBilling = () => navigate('/plans');

  // Locked state: full-screen overlay
  if (isLocked) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-950/30 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
            Trial Expired
          </h2>
          <p className="text-zinc-500 dark:text-zinc-400 mb-6">
            Your free trial has ended. Subscribe to a plan to continue using BlazePOS.
            Your data is safe and will be available once you subscribe.
          </p>
          <Button onClick={goToBilling} size="lg" className="w-full mb-3">
            <CreditCard className="w-4 h-4 mr-2" />
            Choose a Plan
          </Button>
          <p className="text-xs text-zinc-400 dark:text-zinc-500">
            Need help? Contact support@blazepos.app
          </p>
        </div>
      </div>
    );
  }

  // Grace period: urgent red banner
  if (isInGracePeriod) {
    return (
      <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">
            Your trial has expired. You have a few days to subscribe before your account is locked.
          </span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={goToBilling}
          className="bg-white text-red-600 hover:bg-red-50 flex-shrink-0"
        >
          Subscribe Now
        </Button>
      </div>
    );
  }

  // Past due: orange banner
  if (status === 'past_due') {
    return (
      <div className="bg-amber-600 text-white px-4 py-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">
            Your payment failed. Please update your payment method to avoid service interruption.
          </span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={goToBilling}
          className="bg-white text-amber-600 hover:bg-amber-50 flex-shrink-0"
        >
          Update Payment
        </Button>
      </div>
    );
  }

  // Active trial: info banner
  if (isTrialing) {
    const urgency = daysRemaining <= 3 ? 'urgent' : daysRemaining <= 7 ? 'warning' : 'info';

    return (
      <div
        className={cn(
          'px-4 py-2 flex items-center justify-between text-sm',
          urgency === 'urgent' && 'bg-red-600 text-white',
          urgency === 'warning' && 'bg-amber-500 text-white',
          urgency === 'info' && 'bg-blue-600 text-white'
        )}
      >
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>
            {daysRemaining === 0
              ? 'Your trial ends today.'
              : daysRemaining === 1
                ? '1 day left in your free trial.'
                : `${daysRemaining} days left in your free trial.`}
            {' '}Subscribe to keep full access.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={goToBilling}
            className={cn(
              'flex-shrink-0',
              urgency === 'urgent' && 'bg-white text-red-600 hover:bg-red-50',
              urgency === 'warning' && 'bg-white text-amber-600 hover:bg-amber-50',
              urgency === 'info' && 'bg-white text-blue-600 hover:bg-blue-50'
            )}
          >
            Choose a Plan
          </Button>
          <button
            onClick={() => setDismissed(true)}
            className="text-white/70 hover:text-white"
            aria-label="Dismiss trial banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
